import apiClient from './apiClient';
import { formatDateBR } from '../utils/formatters';

/**
 * Dashboard Service
 * Handles dashboard data fetching and aggregation
 */
const dashboardService = {
  /**
   * Fetch all dashboard statistics
   * Uses the optimized /dashboard/stats/ endpoint
   * @returns {Promise<Object>} Dashboard data including KPIs, jobs by status, and recent jobs
   */
  async getDashboardStats() {
    try {
      // Use the optimized dashboard endpoint
      const response = await apiClient.get('/dashboard/stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Get jobs grouped by status
   * @returns {Promise<Array>} Jobs grouped by status with counts
   */
  async getJobsByStatus() {
    try {
      const response = await apiClient.get('/jobs');
      const jobs = response.data.results || response.data || [];

      const statusCounts = jobs.reduce((acc, job) => {
        const status = job.status || 'pending';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      return [
        { status: 'Pendente', count: statusCounts.pending || 0, fill: '#FCD34D' },
        { status: 'Ativo', count: (statusCounts.active || 0) + (statusCounts.in_progress || 0), fill: '#60A5FA' },
        { status: 'Concluído', count: statusCounts.completed || 0, fill: '#34D399' },
        { status: 'Falhou', count: statusCounts.failed || 0, fill: '#F87171' },
      ];
    } catch (error) {
      console.error('Error fetching jobs by status:', error);
      throw error;
    }
  },

  /**
   * Get recent jobs list
   * @param {number} limit - Number of jobs to return (default: 10)
   * @returns {Promise<Array>} Recent jobs with hospital information
   */
  async getRecentJobs(limit = 10) {
    try {
      const [jobsRes, hospitalsRes] = await Promise.all([
        apiClient.get('/jobs'),
        apiClient.get('/hospitals'),
      ]);

      const jobs = jobsRes.data.results || jobsRes.data || [];
      const hospitals = hospitalsRes.data.results || hospitalsRes.data || [];

      return jobs
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, limit)
        .map((job) => ({
          id: job.id,
          hospital: hospitals.find((h) => h.id === job.hospital_id)?.name || 'N/A',
          created_at: formatDateBR(job.created_at),
          status: job.status || 'pending',
        }));
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
      throw error;
    }
  },

  /**
   * Format job status for display
   * @param {string} status - Job status
   * @returns {Object} Formatted status with label and color
   */
  formatJobStatus(status) {
    const statusMap = {
      pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
      active: { label: 'Ativo', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Ativo', color: 'bg-blue-100 text-blue-800' },
      completed: { label: 'Concluído', color: 'bg-green-100 text-green-800' },
      failed: { label: 'Falhou', color: 'bg-red-100 text-red-800' },
    };

    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  },
};

export default dashboardService;
